/**
 * Integrated marketplace hero — industry-specific wrappers over
 * a shared card frame.
 *
 * This file is the marketplace-theme counterpart to the storefront
 * default `StorefrontHero` component. It ports the same integrated
 * card pattern (image-as-backdrop card + scrim + overlaid content
 * + optional date chip + optional trust strip) into the theme-
 * section registry so that merchants who picked a marketplace
 * theme also get the modern hero shape, tuned for their industry.
 *
 * Why per-industry wrappers rather than one big component:
 *   - FASHION reads as a portrait card with refined type, no date
 *     chip, no trust strip — it's an editorial image moment.
 *   - FMCG reads as a landscape card with bold type, date chip,
 *     and a "fast delivery / pay on delivery" trust strip — it's
 *     a daily-needs storefront.
 *   - KIDS reads as a softer rounded card with chunkier type and
 *     playful trust copy.
 *   - GENERAL (covers SERVICES, HOME_SERVICES, GENERAL) mirrors
 *     the default StorefrontHero closely: 16/9 landscape, date
 *     chip, full trust strip.
 *
 * Wrappers consume the same `<IntegratedHeroFrame>` primitive and
 * just pass different defaults. That keeps per-industry code small
 * and the structural changes (slot order, scrim depth, dot styles)
 * centralized in the frame.
 *
 * Public API (each wrapper):
 *   props: { settings, content, globalStyles, store }
 *
 * The wrappers read merchant content (title, subtitle, buttonText,
 * buttonLink, backgroundImage) from `content` — same surface as
 * the old DefaultHero so existing themed merchants don't need any
 * editor changes to switch over.
 */

import React from 'react';
import useStorefrontTheme from '../../hooks/useStorefrontTheme';
import { Link } from 'react-router-dom';
import './sections.css';

/* ----------------------------------------------------------------
 * Routing helpers
 *
 * Storefront has two routing modes:
 *   - Subdomain (e.g. `acme.revstar.com`) → paths like `/shop`
 *   - Path-based (e.g. `revstar.com/s/acme/...`) → paths like
 *     `/s/acme/shop`
 *
 * The hero's CTAs need to land on the right URL in both. Mirrors
 * the `getStorePath` helper in Header/Footer; lifted here so it
 * lives next to the only consumer in this file.
 * ---------------------------------------------------------------- */

const isOnSubdomain = () => {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname;
  return hostname.includes('.') && !hostname.startsWith('www.');
};

const buildStorePath = (store, segment) => {
  const seg = (segment || '').replace(/^\/+/, '');
  if (isOnSubdomain()) {
    return seg ? `/${seg}` : '/';
  }
  const slug = store?.slug;
  if (!slug) return seg ? `/${seg}` : '/';
  return seg ? `/s/${slug}/${seg}` : `/s/${slug}`;
};

/* ----------------------------------------------------------------
 * Icons — slimmer set than StorefrontHero's; marketplace heroes
 * don't carry the live date by default (only some industries do)
 * so we keep the import surface light.
 * ---------------------------------------------------------------- */

const ArrowRightIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12h14M12 5l7 7-7 7" />
  </svg>
);

const ShieldCheckIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <path d="m9 12 2 2 4-4" />
  </svg>
);

const TruckIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M10 17h4V5H2v12h3" />
    <path d="M20 17h2v-3.34a4 4 0 0 0-1.17-2.83L19 9h-5v8h1" />
    <circle cx="7.5" cy="17.5" r="2.5" />
    <circle cx="17.5" cy="17.5" r="2.5" />
  </svg>
);

const LockIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const SparkleIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 3v3m0 12v3M3 12h3m12 0h3M5.6 5.6l2.1 2.1m8.6 8.6 2.1 2.1M5.6 18.4l2.1-2.1m8.6-8.6 2.1-2.1" />
  </svg>
);

const HeartIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  </svg>
);

/* ----------------------------------------------------------------
 * IntegratedHeroFrame — shared presentational primitive.
 *
 * Marketplace heroes don't pull from `store` directly — content
 * (title/subtitle/buttonText/buttonLink/backgroundImage) is
 * merchant-curated via the theme editor and passes through props.
 * That's the contract difference from the storefront-default
 * StorefrontHero, which IS allowed to pull from the live store
 * record because it's the canonical hero.
 * ---------------------------------------------------------------- */

// Builds the inline style for a CTA based on the merchant's
// buttonStyle / buttonRadius / button color settings. Kept inline so
// the frame doesn't need a separate file just for one helper.
//
// `kind` is 'primary' or 'secondary'. The primary CTA defaults to a
// white pill on the scrim; the secondary CTA defaults to a translucent
// outlined pill. Merchant-provided colors override either default
// while keeping the integrated card's overall visual contract.
const buildCtaStyle = ({ kind, buttonStyle, buttonRadius, buttonBgColor, buttonTextColor }) => {
  const style = {};
  if (buttonRadius) style.borderRadius = buttonRadius;
  if (kind === 'primary') {
    if (buttonStyle === 'outline') {
      style.backgroundColor = 'transparent';
      style.borderColor = buttonBgColor || '#ffffff';
      style.color = buttonBgColor || '#ffffff';
    } else if (buttonStyle === 'ghost') {
      style.backgroundColor = 'transparent';
      style.color = buttonBgColor || '#ffffff';
    } else if (buttonBgColor) {
      // filled (default) with merchant brand bg
      style.backgroundColor = buttonBgColor;
      style.color = buttonTextColor || '#ffffff';
    }
    // else: fall back to CSS default (white pill + dark text)
  }
  // Secondary CTA: only honor radius. Color/style overrides live on
  // primary only — secondary is a quiet outlined chip and shouldn't
  // fight the primary for attention. Merchants who really need a
  // bespoke secondary can pick a non-default variant.
  return style;
};

const IntegratedHeroFrame = ({
  // Per-industry visual hooks
  variantClass,           // e.g. 'ts-ihero--fashion'
  aspectRatio,            // CSS aspect-ratio value, e.g. '16/9' or '4/5'
  mobileAspectRatio,      // optional mobile aspect-ratio override
  minHeight,              // CSS min-height value
  scrimVariant = 'normal',// 'soft' | 'normal' | 'deep'
  showDateChip = false,
  showTrustStrip = false,
  trustItems = [],        // [{ icon: Component, label: string }]

  // Content slots
  logo,
  eyebrow,                // optional small label above the title
  title,
  tagline,
  primaryCta,             // { to, label } | null
  secondaryCta,           // { to, label } | null

  // Backdrop
  backgroundImage,
  fallbackColor,

  // Merchant style overrides — passed through from hero-banner
  // settings via adaptProps so the theme editor's color pickers
  // still take effect on the new integrated layout.
  titleColor,
  taglineColor,
  scrimColor,
  scrimOpacity,
  ctaStyles = {},         // { primary, secondary } — output of buildCtaStyle
}) => {
  const { resolvedTheme } = useStorefrontTheme();
  const isDark = resolvedTheme === 'dark';
  // Date chip text — computed inline so the frame doesn't need to
  // run a clock. en-US locale matches the storefront default.
  const dateText = (() => {
    if (!showDateChip) return null;
    const now = new Date();
    const day = now.toLocaleDateString('en-US', { weekday: 'long' });
    const month = now.toLocaleDateString('en-US', { month: 'short' });
    return `${day} · ${month} ${now.getDate()}`;
  })();

  const cardStyle = {
    aspectRatio: aspectRatio,
    minHeight: minHeight,
  };

  // Mobile aspect ratio comes through a CSS custom property so the
  // media query in sections.css can pick it up without a second
  // inline-style branch.
  if (mobileAspectRatio) {
    cardStyle['--ts-ihero-mobile-aspect'] = mobileAspectRatio;
  }

  return (
    <section className={`ts-ihero ${variantClass || ''}`}>
      <div className="ts-ihero-card" style={cardStyle}>
        <div className="ts-ihero-card-bg">
          {backgroundImage ? (
            <img
              src={backgroundImage}
              alt=""
              className="ts-ihero-card-bg-image"
              aria-hidden="true"
            />
          ) : (
            <div
              className="ts-ihero-card-bg-fallback"
              style={{ backgroundColor: fallbackColor || '#111827' }}
              aria-hidden="true"
            />
          )}
        </div>

        {/* Scrim — merchant can override color/opacity via theme
            editor (overlayColor + overlayOpacity). Defaults to the
            per-industry scrimVariant when no override is set. */}
        <div
          className={`ts-ihero-card-scrim ts-ihero-card-scrim--${scrimVariant}`}
          style={scrimColor || scrimOpacity != null ? (() => {
            const op = scrimOpacity != null ? scrimOpacity : 0.5;
            const hex = (scrimColor || '#000000').replace('#', '');
            const r = parseInt(hex.slice(0, 2), 16) || 0;
            const g = parseInt(hex.slice(2, 4), 16) || 0;
            const b = parseInt(hex.slice(4, 6), 16) || 0;
            return { background: `linear-gradient(180deg, rgba(${r},${g},${b},${op * 0.65}) 0%, rgba(${r},${g},${b},${op}) 70%, rgba(${r},${g},${b},${op}) 100%)` };
          })() : undefined}
          aria-hidden="true"
        />

        {showDateChip && (
          <div className="ts-ihero-card-date" aria-hidden="true">
            <span
              className="ts-ihero-card-date-dot"
              style={{ backgroundColor: fallbackColor || '#ffffff' }}
            />
            {dateText}
          </div>
        )}

        <div className="ts-ihero-card-content">
          {logo && (
            <img
              src={logo}
              alt=""
              className="ts-ihero-logo"
              aria-hidden="true"
            />
          )}

          {eyebrow && (
            <span className="ts-ihero-eyebrow">{eyebrow}</span>
          )}

          {title && (
            <h1
              className="ts-ihero-title"
              style={titleColor ? { color: titleColor } : (isDark ? { color: 'var(--theme-dark-text, #f3f4f6)' } : undefined)}
            >
              {title}
            </h1>
          )}

          {tagline && (
            <p
              className="ts-ihero-tagline"
              style={taglineColor ? { color: taglineColor } : (isDark ? { color: 'var(--theme-dark-muted, #9ca3af)' } : undefined)}
            >
              {tagline}
            </p>
          )}

          {(primaryCta || secondaryCta) && (
            <div className="ts-ihero-actions">
              {primaryCta && (
                <Link
                  to={primaryCta.to || '/shop'}
                  className="ts-ihero-cta ts-ihero-cta--primary"
                  style={{ ...(isDark ? { backgroundColor: 'var(--theme-dark-text, #f3f4f6)', color: 'var(--theme-dark-background, #0b0f17)' } : {}), ...ctaStyles.primary }}
                >
                  {primaryCta.label}
                  <ArrowRightIcon size={16} />
                </Link>
              )}
              {/* Secondary CTA supports two modes:
                  1. scrollTargets[]: an ordered list of CSS selectors
                     (e.g. ['.ts-categories-grid', '.ts-featured-products']).
                     Renders a button that scrolls to the first one
                     found on the page — mirrors the default
                     StorefrontHero's onBrowseClick behaviour so both
                     CTAs don't dump customers on the same /shop page.
                  2. to: a Link target (fallback for cases where no
                     section selector applies). */}
              {secondaryCta && (
                secondaryCta.scrollTargets?.length ? (
                  <button
                    type="button"
                    className="ts-ihero-cta ts-ihero-cta--secondary"
                    style={ctaStyles.secondary}
                    onClick={() => {
                      for (const selector of secondaryCta.scrollTargets) {
                        const el = document.querySelector(selector);
                        if (el) {
                          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          return;
                        }
                      }
                    }}
                  >
                    {secondaryCta.label}
                  </button>
                ) : (
                  <Link
                    to={secondaryCta.to || '/'}
                    className="ts-ihero-cta ts-ihero-cta--secondary"
                    style={ctaStyles.secondary}
                  >
                    {secondaryCta.label}
                  </Link>
                )
              )}
            </div>
          )}
        </div>
      </div>

      {showTrustStrip && trustItems.length > 0 && (
        <div className="ts-ihero-trust" aria-label="Store guarantees">
          {trustItems.map((item, idx) => (
            <React.Fragment key={idx}>
              {idx > 0 && <span className="ts-ihero-trust-divider" aria-hidden="true" />}
              <span className="ts-ihero-trust-item">
                {item.icon && <item.icon size={14} />}
                {item.label}
              </span>
            </React.Fragment>
          ))}
        </div>
      )}
    </section>
  );
};

/* ----------------------------------------------------------------
 * Shared prop adapter — pulls the marketplace HeroBanner props
 * into the frame's shape. Each industry wrapper composes on top.
 * ---------------------------------------------------------------- */

const adaptProps = ({ settings, content, globalStyles, store }) => {
  const colors = globalStyles?.colors || {};
  // Primary CTA target: prefer the merchant-curated buttonLink, but
  // route it through buildStorePath so absolute paths like `/shop`
  // work on both subdomain and path-based storefronts. Merchants
  // who pasted a fully-qualified URL (https://...) bypass the
  // helper since buildStorePath only acts on segments.
  const merchantLink = content?.buttonLink;
  const primaryTo = merchantLink && /^https?:\/\//i.test(merchantLink)
    ? merchantLink
    : buildStorePath(store, merchantLink || 'shop');

  // Merchant style overrides from the theme editor. Only forward them
  // when the merchant actually customised the value — for the title /
  // tagline / button colors, the destructure defaults in HeroBanner.js
  // (#ffffff / #e5e5e5 / undefined) match the integrated card's
  // intentional look, so we shouldn't bake those defaults in as inline
  // styles (it would prevent CSS-level theming or future re-styling).
  const isCustomColor = (v, brandDefault) => v && v !== brandDefault;
  const titleColor = isCustomColor(settings?.titleColor, '#ffffff') ? settings.titleColor : null;
  const taglineColor = isCustomColor(settings?.subtitleColor, '#e5e5e5') ? settings.subtitleColor : null;
  const scrimColor = isCustomColor(settings?.overlayColor, '#000000') ? settings.overlayColor : null;
  const scrimOpacity = (settings && settings.overlayOpacity != null && settings.overlayOpacity !== 0.3)
    ? settings.overlayOpacity
    : null;

  // CTA style overrides — also gated so the integrated default (white
  // pill, primary fill) isn't replaced by inline styles unless the
  // merchant actually picked something.
  const buttonStyle = settings?.buttonStyle && settings.buttonStyle !== 'filled' ? settings.buttonStyle : null;
  const buttonRadius = isCustomColor(settings?.buttonRadius, '999px') ? settings.buttonRadius : null;
  const buttonBgColor = settings?.buttonBgColor || null;
  const buttonTextColor = isCustomColor(settings?.buttonTextColor, '#ffffff') ? settings.buttonTextColor : null;

  const ctaStyles = {
    primary: buildCtaStyle({
      kind: 'primary',
      buttonStyle: buttonStyle || (settings?.buttonStyle ?? null),
      buttonRadius,
      buttonBgColor,
      buttonTextColor,
    }),
    secondary: buildCtaStyle({
      kind: 'secondary',
      buttonRadius,
    }),
  };

  return {
    backgroundImage: content?.backgroundImage || '',
    fallbackColor: colors.primary || '#111827',
    logo: store?.logo || null,
    title: content?.title || 'Welcome to Our Store',
    tagline: content?.subtitle || '',
    primaryCta: { to: primaryTo, label: content?.buttonText || 'Shop Now' },
    titleColor,
    taglineColor,
    scrimColor,
    scrimOpacity,
    ctaStyles,
  };
};

/* ----------------------------------------------------------------
 * Industry-specific wrappers
 * ---------------------------------------------------------------- */

// GENERAL — used by GENERAL, SERVICES, HOME_SERVICES. The closest
// mirror to the default StorefrontHero: 16/9 landscape, date chip,
// full trust strip, secondary "Learn More" CTA → /about.
export const GeneralIntegratedHero = (props) => {
  const adapted = adaptProps(props);
  return (
    <IntegratedHeroFrame
      {...adapted}
      variantClass="ts-ihero--general"
      aspectRatio="16/9"
      mobileAspectRatio="4/5"
      minHeight="480px"
      scrimVariant="normal"
      showDateChip
      showTrustStrip
      secondaryCta={{
        scrollTargets: ['.ts-categories-grid', '.ts-featured-products'],
        label: 'Categories',
      }}
      trustItems={[
        { icon: ShieldCheckIcon, label: 'Verified Store' },
        { icon: TruckIcon, label: 'Fast Delivery' },
        { icon: LockIcon, label: 'Secure Checkout' },
      ]}
    />
  );
};

// FASHION — editorial moment. Slightly more cinematic than 16/9
// (16/10 reads "magazine spread") but nowhere near portrait —
// portrait aspect ratios at 1200px wide produce a 1600px tower
// that nobody scrolls past. The fashion feel comes from the
// soft scrim, the eyebrow tag, the lighter type weight, and the
// italic tagline. No date chip, no trust strip (fashion leads
// with image, not reassurance copy).
export const FashionIntegratedHero = (props) => {
  const adapted = adaptProps(props);
  return (
    <IntegratedHeroFrame
      {...adapted}
      variantClass="ts-ihero--fashion"
      aspectRatio="16/10"
      mobileAspectRatio="4/5"
      minHeight="500px"
      scrimVariant="soft"
      eyebrow="The Collection"
      secondaryCta={{
        scrollTargets: ['.ts-featured-products', '.ts-categories-grid'],
        label: 'View Collection',
      }}
    />
  );
};

// FMCG — daily-needs storefront. Landscape, date chip (signals
// "today's prices/deals"), trust strip leans on delivery + payment.
export const FmcgIntegratedHero = (props) => {
  const adapted = adaptProps(props);
  return (
    <IntegratedHeroFrame
      {...adapted}
      variantClass="ts-ihero--fmcg"
      aspectRatio="16/9"
      mobileAspectRatio="4/5"
      minHeight="460px"
      scrimVariant="deep"
      showDateChip
      showTrustStrip
      secondaryCta={{
        scrollTargets: ['.ts-categories-grid', '.ts-featured-products'],
        label: 'Shop by Category',
      }}
      trustItems={[
        { icon: TruckIcon, label: 'Same-day Delivery' },
        { icon: SparkleIcon, label: 'Fresh Stock' },
        { icon: LockIcon, label: 'Secure Payment' },
      ]}
    />
  );
};

// KIDS — softer rounded card, chunkier type (handled in CSS),
// playful trust copy. 5/3 lands a touch taller than 16/9 so the
// card feels chunkier without going full portrait. No date chip
// — kids stores don't need to signal "today" the way grocery does.
export const KidsIntegratedHero = (props) => {
  const adapted = adaptProps(props);
  return (
    <IntegratedHeroFrame
      {...adapted}
      variantClass="ts-ihero--kids"
      aspectRatio="5/3"
      mobileAspectRatio="4/5"
      minHeight="500px"
      scrimVariant="normal"
      showTrustStrip
      secondaryCta={{
        scrollTargets: ['.ts-categories-grid', '.ts-featured-products'],
        label: 'Browse',
      }}
      trustItems={[
        { icon: HeartIcon, label: 'Parent-approved' },
        { icon: ShieldCheckIcon, label: 'Tested safe' },
        { icon: TruckIcon, label: 'Fast Delivery' },
      ]}
    />
  );
};

export { IntegratedHeroFrame };
